/**
 * Errors and rejections (docs/design/03 §2, docs/design/08 §1.1).
 *
 * The engine never throws on bad *input*: an invalid command returns a {@link Rejection} value and the
 * state is unchanged. It throws {@link EngineError} only when an invariant is broken (a bug) or a host
 * hands it something structurally impossible (an invalid setup, an unknown schema). Hosts log and alert
 * on `EngineError` and keep the last good state.
 */
import type { JsonObject } from './json';

/** Why the engine refused a command. Append-only: hosts and clients switch on these codes. */
export const REJECTION_CODES = [
  // Generic
  'invalidCommand', // structurally invalid payload that slipped past the protocol schemas
  'gameOver', // the game has a result
  'notAPlayer', // the actor is not a seat in this game, or has left it (conceded)
  'notPermitted', // the actor may not perform this action (e.g. claim control for someone else)
  'noChange', // the command would not change anything (no event, no replacement: CR 415.1.b analogue)
  'outOfRange', // a number is outside its allowed range
  'notImplemented', // the command exists in the protocol but this tier's layer does not implement it yet
  'tierUnavailable', // the match's tier has no engine layer in this build
  'wrongTier', // the command is not accepted at this match's tier
  // Objects and zones
  'unknownObject', // no such object *visible to the actor* (hidden objects are indistinguishable from missing ones)
  'notYours', // the actor neither controls (board) nor owns (other zones) the object
  'invalidDestination', // the object can't go there (wrong category, another player's zone, CR 056)
  'immovable', // battlefields and the Champion Legend never move (CR 170.4, 107.4.d)
  'emptyZone', // the zone has no cards to draw, channel or look at
  'unknownZone', // the zone reference is malformed
  'unknownBattlefield',
  'unknownChainItem',
  'unknownToken',
  'unknownPlayer',
  // Turn structure
  'notYourTurn', // turn markers (phase, end turn) belong to the turn player
  'setupInProgress', // table commands wait until setup (battlefields, mulligans) is complete
  // Decisions
  'decisionPending', // another decision must be answered first
  'noDecision', // there is no pending decision
  'staleDecision', // the command quotes a decision that is no longer pending
  'notYourDecision',
  'invalidAnswer', // the answer is not one of the offered options or breaks min/max
  // Undo
  'undoUnavailable', // nothing the actor may undo (barrier, another player's step, empty history)
  'undoPending', // an undo request is already open
  'noUndoRequest', // there is no open request to answer
] as const;

export type RejectionCode = (typeof REJECTION_CODES)[number];

/** A refused command. `detail` may only echo the command or describe public facts (never hidden state). */
export interface Rejection {
  readonly rejected: RejectionCode;
  readonly detail?: string;
}

/** Builds a {@link Rejection}. */
export function reject(code: RejectionCode, detail?: string): Rejection {
  return detail === undefined ? { rejected: code } : { rejected: code, detail };
}

/** True when `value` is a {@link Rejection} (as opposed to a successful step). */
export function isRejection(value: unknown): value is Rejection {
  return typeof value === 'object' && value !== null && 'rejected' in value;
}

/** Categories of {@link EngineError}. */
export type EngineErrorCode =
  | 'invariant' // an internal invariant was broken: a bug in the engine
  | 'invalidSetup' // createGame received an impossible setup (host bug: decks are validated before)
  | 'notJson' // a value that must be plain JSON is not (functions, undefined, NaN, cycles)
  | 'unsupportedSchema' // a stored state has a schema this build can't migrate
  | 'replayDivergence'; // a recorded command was rejected or a recorded hash differs during replay

/** A broken invariant or impossible host input. Never thrown for bad player input. */
export class EngineError extends Error {
  override readonly name = 'EngineError';
  readonly code: EngineErrorCode;
  readonly details: JsonObject | undefined;

  constructor(code: EngineErrorCode, message: string, details?: JsonObject) {
    super(message);
    this.code = code;
    this.details = details;
  }
}

/** Throws an `invariant` {@link EngineError} when `condition` is falsy. */
export function invariant(condition: unknown, message: string, details?: JsonObject): asserts condition {
  if (!condition) throw new EngineError('invariant', message, details);
}

/** Exhaustiveness helper for discriminated unions. */
export function assertNever(value: never, what = 'value'): never {
  throw new EngineError('invariant', `Unexpected ${what}: ${JSON.stringify(value)}`);
}
